import { BaseApiStore } from './core/base.api.store';
import { action, computed, makeObservable, observable } from 'mobx';
import { SessionStore } from './session.store';

export enum AggregateFunction {
	SUM = 'SUM',
	AVG = 'AVG',
	MIN = 'MIN',
	MAX = 'MAX',
	COUNT = 'COUNT',
	COUNT_DISTINCT = 'COUNT_DISTINCT',
	NONE = 'NONE',
}

export enum JoinType {
	INNER = 'INNER',
	LEFT = 'LEFT',
	RIGHT = 'RIGHT',
	FULL = 'FULL',
}

export interface IBmCount {
	bm: number;
	count: number;
}

export interface IVntrBmCountQuality {
	latestBm: number;
	latestMonthRecords: number;
	historicalAvg: number;
	historicalMin: number;
	historicalMax: number;
	deviationPct: number;
	status: string;
}

export interface IFieldBmQuality {
	fieldName: string;
	latestBm: number;
	latestValue: number;
	historicalAvg: number;
	historicalMin: number;
	historicalMax: number;
	deviationPct: number;
	status: string;
}

export type TimeDimension = 'bm' | 'kw' | 'year';

export interface ITimeSeriesPoint {
	timeDimension: number;
	value: number;
	isLatest: boolean;
}

export interface ITimeSeriesWithStats {
	data: ITimeSeriesPoint[];
	avg: number;
	min: number;
	max: number;
	stdDev: number;
	upperLimit: number; // avg + 2*stdDev
	lowerLimit: number; // avg - 2*stdDev
}

// Legacy aliases for backwards compatibility
export type IBmTimeSeriesPoint = ITimeSeriesPoint;
export type IBmTimeSeriesWithStats = ITimeSeriesWithStats;

export interface IKwCount {
	kw: number;
	count: number;
}
export interface IYearCount {
	year: number;
	count: number;
}
export interface CaseExpression {
	whenCondition: string;
	thenValue: string;
	elseValue: string;
}

export interface JoinClause {
	type: JoinType;
	table: string;
	alias?: string;
	condition: string;
}

export interface SelectColumn {
	column?: string;
	aggregateFunction?: AggregateFunction;
	alias?: string;
	groupBy?: boolean;
	distinct?: boolean;
	caseExpression?: CaseExpression;
}

export interface AgtAnalyticsRequestDto {
	select: SelectColumn[];
	from: string;
	where?: string;
	joins?: JoinClause[];
	orderBy?: string;
	orderDirection?: 'ASC' | 'DESC';
	limit?: number;
}

export interface AnalyticsConfig {
	tableName: string;
	defaultColumns?: SelectColumn[];
	defaultWhere?: string;
	defaultJoins?: JoinClause[];
	defaultOrderBy?: string;
	defaultOrderDirection?: 'ASC' | 'DESC';
	defaultLimit?: number;
}

export class AgtAnalyticsStore extends BaseApiStore {
	apiPath = 'api/analytics';
	queryName = '?setthisfornetworkmonitoring';

	@observable
	loading: boolean = false;

	@observable
	data: any[] = [];

	@observable
	error: string | null = null;

	@observable
	config: AnalyticsConfig;

	@observable
	cache: Map<string, any[]> = new Map();

	@observable
	pendingRequests: Map<string, Promise<any[]>> = new Map();

	constructor(session: SessionStore, config: AnalyticsConfig) {
		super(session);
		makeObservable(this);
		this.config = config;
	}

	@action
	setLoading(loading: boolean) {
		this.loading = loading;
	}

	@action
	setData(data: any[]) {
		this.data = data;
	}

	@action
	setError(error: string | null) {
		this.error = error;
	}

	@computed
	get hasData() {
		return this.data && this.data.length > 0;
	}

	@computed
	get isConfigured() {
		return this.config !== null && this.config.tableName !== undefined;
	}

	buildRequest(selectColumns?: SelectColumn[], whereClause?: string, joins?: JoinClause[]): AgtAnalyticsRequestDto {
		const req: AgtAnalyticsRequestDto = {
			select: selectColumns || this.config.defaultColumns || [],
			from: this.config.tableName,
			where: whereClause || this.config.defaultWhere,
			joins: joins || this.config.defaultJoins,
		};
		if (this.config.defaultOrderBy) {
			req.orderBy = this.config.defaultOrderBy;
			req.orderDirection = this.config.defaultOrderDirection;
		}
		if (this.config.defaultLimit) {
			req.limit = this.config.defaultLimit;
		}
		return req;
	}

	@action
	async executeQuery(request: AgtAnalyticsRequestDto): Promise<any[]> {
		try {
			this.setLoading(true);
			this.setError(null);

			const response = await this.post('/agt' + this.queryName, request);

			if (response.status && response.status >= 400) {
				throw new Error(response.message || 'Analytics query failed');
			}

			const result = Array.isArray(response.data) ? response.data : [];
			this.setData(result);

			return result;
		} catch (error: any) {
			const errorMessage = error.message || 'Unknown error occurred';
			this.setError(errorMessage);
			throw error;
		} finally {
			this.setLoading(false);
		}
	}

	private createRequestHash(request: AgtAnalyticsRequestDto): string {
		// Create a deterministic hash from the request object
		const hashData = {
			tableName: request.from,
			select: request.select?.map(col => ({
				column: col.column,
				aggregateFunction: col.aggregateFunction,
				alias: col.alias,
				groupBy: col.groupBy,
				distinct: col.distinct,
				caseExpression: col.caseExpression
			})),
			where: request.where,
			joins: request.joins?.map(join => ({
				type: join.type,
				table: join.table,
				alias: join.alias,
				condition: join.condition
			})),
			orderBy: request.orderBy,
			orderDirection: request.orderDirection,
			limit: request.limit,
		};

		// Simple hash function using JSON stringify and character codes
		const jsonString = JSON.stringify(hashData);
		let hash = 0;
		for (let i = 0; i < jsonString.length; i++) {
			const char = jsonString.charCodeAt(i);
			hash = ((hash << 5) - hash) + char;
			hash = hash & hash; // Convert to 32-bit integer
		}
		return `query_${Math.abs(hash).toString(36)}`;
	}

	@action
	async query(selectColumns?: SelectColumn[], whereClause?: string, joins?: JoinClause[]): Promise<any[]> {
		const request = this.buildRequest(selectColumns, whereClause, joins);
		const cacheKey = this.createRequestHash(request);

		// Check cache first
		const cached = this.getCachedByKey(cacheKey);
		if (cached) {
			return cached;
		}

		// Check if there's already a pending request with the same hash
		const pendingRequest = this.pendingRequests.get(cacheKey);
		if (pendingRequest) {
			// Wait for the existing request to complete and return its result
			return pendingRequest;
		}

		// Create and store the pending request
		const requestPromise = this.executeQuery(request).then(result => {
			// Cache the result and remove from pending requests
			this.cacheByKey(cacheKey, result);
			this.pendingRequests.delete(cacheKey);
			return result;
		}).catch(error => {
			// Remove from pending requests on error
			this.pendingRequests.delete(cacheKey);
			throw error;
		});

		// Store the pending request
		this.pendingRequests.set(cacheKey, requestPromise);

		return requestPromise;
	}

	@action
	async queryWithRequest(request: AgtAnalyticsRequestDto): Promise<any[]> {
		const cacheKey = this.createRequestHash(request);

		const cached = this.getCachedByKey(cacheKey);
		if (cached) {
			return cached;
		}

		const pendingRequest = this.pendingRequests.get(cacheKey);
		if (pendingRequest) {
			return pendingRequest;
		}

		const requestPromise = this.executeQuery(request).then(result => {
			this.cacheByKey(cacheKey, result);
			this.pendingRequests.delete(cacheKey);
			return result;
		}).catch(error => {
			this.pendingRequests.delete(cacheKey);
			throw error;
		});

		this.pendingRequests.set(cacheKey, requestPromise);
		return requestPromise;
	}

	@action
	async queryWithCustomSelect(selectColumns: SelectColumn[]): Promise<any[]> {
		return this.query(selectColumns);
	}

	@action
	async queryWithWhere(whereClause: string): Promise<any[]> {
		return this.query(undefined, whereClause);
	}

	@action
	async queryWithJoins(joins: JoinClause[], selectColumns?: SelectColumn[], whereClause?: string): Promise<any[]> {
		return this.query(selectColumns, whereClause, joins);
	}

	@action
	async aggregateBy(
		aggregateColumn: string,
		aggregateFunction: AggregateFunction,
		groupByColumns: string[],
		whereClause?: string,
		joins?: JoinClause[]
	): Promise<any[]> {
		const selectColumns: SelectColumn[] = [
			...groupByColumns.map(col => ({
				column: col,
				aggregateFunction: AggregateFunction.NONE,
				groupBy: true,
			})),
			{
				column: aggregateColumn,
				aggregateFunction: aggregateFunction,
				alias: `${aggregateFunction.toLowerCase()}_${aggregateColumn}`,
			},
		];

		return this.query(selectColumns, whereClause, joins);
	}

	@action
	async aggregateWithCase(
		caseExpression: CaseExpression,
		aggregateFunction: AggregateFunction,
		alias: string,
		groupByColumns: string[],
		whereClause?: string,
		joins?: JoinClause[]
	): Promise<any[]> {
		const selectColumns: SelectColumn[] = [
			...groupByColumns.map(col => ({
				column: col,
				aggregateFunction: AggregateFunction.NONE,
				groupBy: true,
			})),
			{
				aggregateFunction: aggregateFunction,
				alias: alias,
				caseExpression: caseExpression,
			},
		];

		return this.query(selectColumns, whereClause, joins);
	}

	createCaseColumn(
		caseExpression: CaseExpression,
		aggregateFunction: AggregateFunction,
		alias: string
	): SelectColumn {
		return {
			aggregateFunction: aggregateFunction,
			alias: alias,
			caseExpression: caseExpression,
		};
	}

	@action
	clear() {
		this.data = [];
		this.error = null;
		this.cache.clear();
		this.pendingRequests.clear();
	}

	@action
	cacheByKey(key: string, data: any[]): any[] {
		this.cache.set(key, data);
		return data;
	}

	@action
	getCachedByKey(key: string): any[] | undefined {
		return this.cache.get(key);
	}

	@action
	clearCacheByKey(key: string): void {
		this.cache.delete(key);
	}

	async countBmData(): Promise<IBmCount[]> {
		const selectColumns: SelectColumn[] = [
			{ column: 'bm', aggregateFunction: AggregateFunction.NONE, distinct: true, alias: 'bm', groupBy: true, },
			{ column: 'vtnr_id', aggregateFunction: AggregateFunction.COUNT, distinct: true, alias: 'count' },
		];
		const results: IBmCount[] = await this.query(selectColumns);
		return results.sort((a, b) => b.bm - a.bm);
	}

	async countKwData(): Promise<IKwCount[]> {
		const selectColumns: SelectColumn[] = [
			{ column: 'kw', aggregateFunction: AggregateFunction.NONE, distinct: true, alias: 'kw', groupBy: true, },
			{ column: 'vtnr_id', aggregateFunction: AggregateFunction.COUNT, distinct: true, alias: 'count' },
		];
		const results: IKwCount[] = await this.query(selectColumns);
		return results.sort((a, b) => b.kw - a.kw);
	}

	async countYearData(): Promise<IYearCount[]> {
		const selectColumns: SelectColumn[] = [
			{ column: 'bm', aggregateFunction: AggregateFunction.NONE, distinct: true, alias: 'year', groupBy: true, },
			{ column: 'vtnr_id', aggregateFunction: AggregateFunction.COUNT, distinct: true, alias: 'count' },
		];
		const results: IYearCount[] = await this.query(selectColumns);
		return results.sort((a, b) => b.year - a.year);
	}


	async countDistincVtnr(): Promise<IBmCount[]> {
		const selectColumns: SelectColumn[] = [
			{ column: 'vtnr_id', aggregateFunction: AggregateFunction.COUNT_DISTINCT, distinct: true, alias: 'count' },
		];
		return await this.query(selectColumns);
	}

	private calculateStdDev(values: number[], avg: number): number {
		if (values.length === 0) return 0;
		const squaredDiffs = values.map(v => Math.pow(v - avg, 2));
		const avgSquaredDiff = squaredDiffs.reduce((sum, val) => sum + val, 0) / values.length;
		return Math.sqrt(avgSquaredDiff);
	}

	async getVntrBmTimeSeries(timeDimension: TimeDimension = 'bm', where?: string): Promise<ITimeSeriesWithStats> {
		const selectColumns: SelectColumn[] = [
			{ column: timeDimension, aggregateFunction: AggregateFunction.NONE, groupBy: true, alias: 'td' },
			{ column: 'vtnr_id', aggregateFunction: AggregateFunction.COUNT_DISTINCT, alias: 'value' },
		];
		const results: { td: number; value: number }[] = await this.query(selectColumns, where);

		if (results.length === 0) {
			return { data: [], avg: 0, min: 0, max: 0, stdDev: 0, upperLimit: 0, lowerLimit: 0 };
		}

		const sorted = [...results].sort((a, b) => a.td - b.td);
		const latestTimeDimension = sorted[sorted.length - 1].td;
		const values = sorted.map(r => r.value);

		const avg = values.reduce((sum, v) => sum + v, 0) / values.length;
		const min = Math.min(...values);
		const max = Math.max(...values);
		const stdDev = this.calculateStdDev(values, avg);

		return {
			data: sorted.map(r => ({
				timeDimension: r.td,
				value: r.value,
				isLatest: r.td === latestTimeDimension,
			})),
			avg: Math.round(avg),
			min,
			max,
			stdDev: Math.round(stdDev),
			upperLimit: Math.round(avg + 2 * stdDev),
			lowerLimit: Math.round(Math.max(0, avg - 2 * stdDev)),
		};
	}

	async getFieldBmTimeSeries(fieldName: string, aggregateFunc: AggregateFunction = AggregateFunction.SUM, timeDimension: TimeDimension = 'bm', where?: string): Promise<ITimeSeriesWithStats> {
		const selectColumns: SelectColumn[] = [
			{ column: timeDimension, aggregateFunction: AggregateFunction.NONE, groupBy: true, alias: 'td' },
			{ column: fieldName, aggregateFunction: aggregateFunc, alias: 'value' },
		];
		const results: { td: number; value: number }[] = await this.query(selectColumns, where);

		if (results.length === 0) {
			return { data: [], avg: 0, min: 0, max: 0, stdDev: 0, upperLimit: 0, lowerLimit: 0 };
		}

		const sorted = [...results].sort((a, b) => a.td - b.td);
		const latestTimeDimension = sorted[sorted.length - 1].td;
		const values = sorted.map(r => r.value || 0);

		const avg = values.reduce((sum, v) => sum + v, 0) / values.length;
		const min = Math.min(...values);
		const max = Math.max(...values);
		const stdDev = this.calculateStdDev(values, avg);

		return {
			data: sorted.map(r => ({
				timeDimension: r.td,
				value: r.value || 0,
				isLatest: r.td === latestTimeDimension,
			})),
			avg: Math.round(avg * 100) / 100,
			min,
			max,
			stdDev: Math.round(stdDev * 100) / 100,
			upperLimit: Math.round((avg + 2 * stdDev) * 100) / 100,
			lowerLimit: Math.round(Math.max(0, avg - 2 * stdDev) * 100) / 100,
		};
	}
}
